/**
 * weapon-vfx-sprites.ts — v4M4.f.1 — per-family commissioned bitmap
 * sprite cache + loader + render-pipeline helpers for weapon VFX
 * (Beam / Pulse / Burst / Railgun / Torpedo).
 *
 * Architectural twin of `vfx.ts:1185+` missile-sprite scaffolding
 * (v3M28.6). Key differences:
 *
 *   1. Multi-frame animation support — a single sprite key can resolve
 *      to a frame array (numbered series `<key>-f0.png`, `<key>-f1.png`,
 *      ...) rather than a single image. Renderer picks the active frame
 *      per `pickAnimationFrame(...)` below.
 *   2. Per-Mk-tier colour tinting — tinted families (Beam / Pulse /
 *      Railgun / Torpedo) generate **neutral** sprites (white core +
 *      grey halo) and apply Mk-tier colour at draw time via the
 *      `drawTintedSprite(...)` helper. Mk-tier colour table lives here
 *      as code constants (not xlsx — pure visual, no balance impact).
 *   3. Additive-blend wrapper — multi-layer Beam glow + Pulse halo +
 *      Torpedo plasma halo + Railgun EM all stack via `'lighter'`
 *      composite. `withAdditiveBlend(ctx, fn)` wraps the blit calls.
 *
 * Procedural fallback (existing weapon-VFX draws in `vfx.ts`) stays
 * active wherever `getWeaponVfxSprite(...)` returns null — game ships
 * fully without commissioned sprites; Alpha 0.6 visual read is
 * preserved verbatim until assets land in `public/vfx/weapons/`.
 *
 * Path convention: `/vfx/weapons/<family>/<key>.png` for static
 * sprites; `/vfx/weapons/<family>/<key>-f<N>.png` for multi-frame
 * animations. Keepers should land at `public/vfx/weapons/<family>/`.
 *
 * Authoritative design lives in `docs/WEAPON_VFX.md`. Per-family sprite
 * breakdown is §2; per-Mk colour mapping is §3; render-pipeline
 * contract (additive blend + tinting + frame sequencer) is §6.
 */

// ---------------------------------------------------------------------------
// Type model — families, keys, frame metadata
// ---------------------------------------------------------------------------

export type WeaponFamily = 'beam' | 'pulse' | 'burst' | 'railgun' | 'torpedo';

/** Animation playback mode for multi-frame sprites. */
export type AnimationMode = 'one-shot' | 'loop';

interface SpriteDefinition {
  family: WeaponFamily;
  frameCount: number;
  /** Sim-ticks per displayed frame; default 2 (5 fps at SIM_TICK_HZ=10). */
  ticksPerFrame: number;
  mode: AnimationMode;
}

/**
 * Single source of truth for sprite keys + frame metadata. Per
 * `docs/WEAPON_VFX.md §2`. The `WeaponVfxSpriteKey` type derives from
 * `keyof typeof SPRITE_DEFINITIONS` so TypeScript catches typos at
 * call sites.
 */
const SPRITE_DEFINITIONS = {
  // Beam (§2.1)
  'beam-charge-up': { family: 'beam', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'beam-muzzle-flare': { family: 'beam', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'beam-core': { family: 'beam', frameCount: 1, ticksPerFrame: 1, mode: 'loop' },
  'beam-halo': { family: 'beam', frameCount: 1, ticksPerFrame: 1, mode: 'loop' },
  'beam-particle-trail': { family: 'beam', frameCount: 1, ticksPerFrame: 1, mode: 'loop' },
  // MFix15 — reduced from 4 frames to 1 per the 2026-05-24 manual-
  // sourced cohort. Michael delivered f0 + f1 candidates and chose to
  // drop the multi-frame animation rather than mix new f0 with the
  // existing v4M4.f f2/f3 (stylistic inconsistency). Source file is
  // now the single `beam-impact-burst.png` (no -fN suffix).
  'beam-impact-burst': { family: 'beam', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },

  // Pulse (§2.2)
  'pulse-charge-bloom': { family: 'pulse', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'pulse-muzzle-flare': { family: 'pulse', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'pulse-bolt': { family: 'pulse', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'pulse-impact-burst': { family: 'pulse', frameCount: 3, ticksPerFrame: 2, mode: 'one-shot' },
  'pulse-spark-ripple': { family: 'pulse', frameCount: 3, ticksPerFrame: 2, mode: 'one-shot' },

  // Burst (§2.3) — single palette, cadence via xlsx
  'burst-muzzle-flash': { family: 'burst', frameCount: 2, ticksPerFrame: 2, mode: 'one-shot' },
  'burst-tracer-streak': { family: 'burst', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'burst-impact-spark': { family: 'burst', frameCount: 3, ticksPerFrame: 2, mode: 'one-shot' },
  'burst-casing-eject': { family: 'burst', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },

  // Railgun (§2.4)
  'railgun-charge-coil': { family: 'railgun', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'railgun-muzzle-discharge': { family: 'railgun', frameCount: 2, ticksPerFrame: 2, mode: 'one-shot' },
  'railgun-slug-streak': { family: 'railgun', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'railgun-impact-shockwave': { family: 'railgun', frameCount: 4, ticksPerFrame: 2, mode: 'one-shot' },
  'railgun-impact-debris': { family: 'railgun', frameCount: 3, ticksPerFrame: 2, mode: 'one-shot' },

  // Torpedo (§2.5)
  'torpedo-launch-flash': { family: 'torpedo', frameCount: 2, ticksPerFrame: 2, mode: 'one-shot' },
  'torpedo-plasma-core': { family: 'torpedo', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'torpedo-plasma-halo': { family: 'torpedo', frameCount: 1, ticksPerFrame: 1, mode: 'one-shot' },
  'torpedo-impact-explosion': { family: 'torpedo', frameCount: 4, ticksPerFrame: 2, mode: 'one-shot' },
  'torpedo-shockwave-ring': { family: 'torpedo', frameCount: 3, ticksPerFrame: 2, mode: 'one-shot' },
  // MFix15 — new animated in-flight torpedo sprite delivered in the
  // 2026-05-24 manual-sourced cohort. 3-frame loop cycled during the
  // torpedo's flight phase (TRACER_DURATION_BY_SUBTYPE.torpedo = 28
  // ticks). When loaded, `drawTorpedoSprite` in `vfx.ts` replaces the
  // static `torpedo-plasma-core` with the animated travel frame —
  // `torpedo-plasma-halo` stays as the outer-glow overlay.
  'torpedo-travel': { family: 'torpedo', frameCount: 3, ticksPerFrame: 2, mode: 'loop' },
} as const satisfies Record<string, SpriteDefinition>;

export type WeaponVfxSpriteKey = keyof typeof SPRITE_DEFINITIONS;

/**
 * Resolved sprite data returned by `getWeaponVfxSprite(...)`. The
 * `frames` array length equals `meta.frameCount`. Renderer picks the
 * active frame via `pickAnimationFrame(...)` for animated sprites or
 * just uses `frames[0]` for statics.
 */
export interface WeaponVfxSpriteData {
  frames: HTMLImageElement[];
  meta: SpriteDefinition;
}

/** Look up the static frame metadata for a sprite key. Pure read; no load. */
export function getWeaponVfxSpriteMeta(key: WeaponVfxSpriteKey): SpriteDefinition {
  return SPRITE_DEFINITIONS[key];
}

// ---------------------------------------------------------------------------
// Mk-tier colour tables — per `docs/WEAPON_VFX.md §3`
// ---------------------------------------------------------------------------
//
// Mapping is by Mk INDEX, not Mk MAX. A weapon with only Mk1-Mk3 lives
// at yellow / yellow / green (Beam) and never reaches the highest-tier
// colour. The lookup just floors at the table's defined keys.
//
// Pure visual constants — no balance impact — so they stay in code
// rather than xlsx per the v3M14 / v3M27 convention.

const BEAM_TIER_COLOURS: Readonly<Record<number, string>> = {
  1: '#FFD600', 2: '#FFD600', // yellow (Mk1-2)
  3: '#00C853', 4: '#00C853', // green (Mk3-4)
  5: '#2962FF', 6: '#2962FF', // blue (Mk5-6)
};

const PULSE_TIER_COLOURS: Readonly<Record<number, string>> = {
  1: '#D32F2F', 2: '#D32F2F', // red (Mk1-2)
  3: '#F0A93B', 4: '#F0A93B', // orange (Mk3-4, Defiant canonical)
  5: '#FFEB3B', 6: '#FFEB3B', // white-hot yellow (Mk5-6)
};

const RAILGUN_TIER_COLOURS: Readonly<Record<number, string>> = {
  1: '#FFC107', 2: '#FFC107', // yellow EM (Mk1-2)
  3: '#82B1FF', 4: '#82B1FF', // white-blue EM (Mk3-4, Expanse canonical)
  5: '#9C27B0', 6: '#9C27B0', // purple-violet EM (Mk5-6)
};

const TORPEDO_TIER_COLOURS: Readonly<Record<number, string>> = {
  1: '#FF6F00', 2: '#FF6F00', // orange (Mk1-2)
  3: '#E04A24', 4: '#E04A24', // red-orange (Mk3-4, photon-torpedo canonical)
  5: '#B71C1C', 6: '#B71C1C', // crimson (Mk5-6)
};

/**
 * Return the Mk-tier tint colour for a tinted family at the given Mk
 * index. Burst is single-palette per §3.5 and returns null (renderer
 * skips the tint pass for Burst sprites).
 *
 * For tinted families, Mk indices outside the 1..6 table clamp toward
 * the nearest defined tier (Mk < 1 → Mk1 colour; Mk > 6 → Mk6 colour).
 * In practice Mk should always be 1..6 per the v1 tech-tree.
 */
export function getMkTierColour(
  family: WeaponFamily,
  mkIndex: number,
): string | null {
  if (family === 'burst') return null;
  const table =
    family === 'beam'
      ? BEAM_TIER_COLOURS
      : family === 'pulse'
        ? PULSE_TIER_COLOURS
        : family === 'railgun'
          ? RAILGUN_TIER_COLOURS
          : TORPEDO_TIER_COLOURS;
  const clamped = Math.max(1, Math.min(6, Math.round(mkIndex)));
  return table[clamped] ?? table[1]!;
}

/**
 * Parse `Mk<N>` suffix from a weapon name string (e.g. `Energy Beam
 * Weapon Mk3` → `3`). Returns `1` when no suffix is found (defensive
 * — every weapon should carry an Mk in v1, but the fallback keeps the
 * renderer green if a future xlsx row omits it).
 */
export function parseMkIndex(weaponName: string): number {
  const match = weaponName.match(/Mk(\d+)/);
  return match ? Number.parseInt(match[1]!, 10) : 1;
}

// ---------------------------------------------------------------------------
// Sprite-loader infrastructure
// ---------------------------------------------------------------------------
//
// Lazy-load on first `getWeaponVfxSprite(...)` call. Same shape as the
// v3M28 missile loader: a slot exists from the moment loading starts;
// `resolved` flips true on success OR failure; `ok` is true only when
// every frame loaded successfully (multi-frame animations are
// all-or-nothing).
//
// Headless-safe: vitest 'node' env has no `Image`, so the slot
// resolves immediately as `{ ok: false }` and the getter returns null,
// letting the procedural fallback take over.

interface WeaponVfxSpriteSlot {
  /** One entry per frame. Same length as the definition's frameCount. */
  images: (HTMLImageElement | null)[];
  /** True once every frame has resolved (load succeeded or failed). */
  resolved: boolean;
  /** True only when ALL frames loaded successfully. */
  ok: boolean;
}

const weaponVfxSpriteSlots: Map<WeaponVfxSpriteKey, WeaponVfxSpriteSlot> =
  new Map();
const weaponVfxSpriteSrcOverrides: Map<WeaponVfxSpriteKey, string[]> =
  new Map();

/**
 * Override the URL(s) the loader uses for a given sprite key. Useful
 * when integration prefers `import url from './path.png?url'` (Vite
 * rewrites to a hashed URL) over the default
 * `/vfx/weapons/<family>/<key>(-f<N>).png` convention.
 *
 * For multi-frame sprites, pass an array of URLs (one per frame, in
 * order). For static sprites, a single-element array.
 *
 * Call BEFORE the first `getWeaponVfxSprite(key)` for the key. After
 * loading kicks off, the override has no effect.
 */
export function setWeaponVfxSpriteSrc(
  key: WeaponVfxSpriteKey,
  urls: string[],
): void {
  weaponVfxSpriteSrcOverrides.set(key, urls);
}

function defaultUrlFor(key: WeaponVfxSpriteKey, frameIndex: number): string {
  const def = SPRITE_DEFINITIONS[key];
  if (def.frameCount === 1) {
    return `/vfx/weapons/${def.family}/${key}.png`;
  }
  return `/vfx/weapons/${def.family}/${key}-f${frameIndex}.png`;
}

/**
 * Return resolved sprite data for the given key, or null when no
 * sprite has loaded yet (or loading failed). On first call for a key,
 * triggers async loading of all frames and immediately returns null —
 * subsequent calls return the data once every frame is in.
 *
 * Renderer consumers should branch on null and fall through to the
 * existing procedural draw path.
 */
export function getWeaponVfxSprite(
  key: WeaponVfxSpriteKey,
): WeaponVfxSpriteData | null {
  const cached = weaponVfxSpriteSlots.get(key);
  if (cached) {
    if (!cached.resolved || !cached.ok) return null;
    return {
      frames: cached.images as HTMLImageElement[],
      meta: SPRITE_DEFINITIONS[key],
    };
  }
  // Headless guard — vitest 'node' env doesn't define Image.
  if (typeof Image === 'undefined') {
    const def = SPRITE_DEFINITIONS[key];
    weaponVfxSpriteSlots.set(key, {
      images: new Array(def.frameCount).fill(null),
      resolved: true,
      ok: false,
    });
    return null;
  }
  // Kick off async load for every frame.
  const def = SPRITE_DEFINITIONS[key];
  const overrides = weaponVfxSpriteSrcOverrides.get(key);
  const slot: WeaponVfxSpriteSlot = {
    images: new Array(def.frameCount).fill(null),
    resolved: false,
    ok: false,
  };
  weaponVfxSpriteSlots.set(key, slot);
  let pending = def.frameCount;
  let allOk = true;
  for (let i = 0; i < def.frameCount; i++) {
    const image = new Image();
    slot.images[i] = image;
    const finalise = (succeeded: boolean): void => {
      if (!succeeded) allOk = false;
      pending--;
      if (pending === 0) {
        slot.resolved = true;
        slot.ok = allOk;
      }
    };
    image.onload = () => {
      finalise(image.naturalWidth > 0 && image.naturalHeight > 0);
    };
    image.onerror = () => {
      finalise(false);
    };
    image.src = overrides?.[i] ?? defaultUrlFor(key, i);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Render-pipeline helpers (§6 of `docs/WEAPON_VFX.md`)
// ---------------------------------------------------------------------------

/**
 * Wrap a draw callback in additive-blend composite mode. Used for
 * multi-layer Beam glow, Pulse halos, Torpedo plasma halos, and
 * Railgun EM glow per §6.2. Default composite is restored on exit.
 *
 * Pattern:
 *   withAdditiveBlend(ctx, () => {
 *     ctx.drawImage(beamCoreSprite, x, y, w, h);
 *     ctx.drawImage(beamHaloSprite, x, y, w, h);
 *   });
 */
export function withAdditiveBlend(
  ctx: CanvasRenderingContext2D,
  fn: () => void,
): void {
  const prev = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = 'lighter';
  try {
    fn();
  } finally {
    ctx.globalCompositeOperation = prev;
  }
}

/**
 * Draw a sprite then apply a tier-colour tint using the `'source-atop'`
 * composite pattern from §6.3. The tint paints only within the sprite's
 * existing alpha; pixels outside the silhouette stay untouched.
 *
 * Single draw call per sprite (no offscreen canvas needed). Pass
 * `tierColour = null` to skip the tint pass (used for Burst sprites
 * and for tinted families when the renderer wants the neutral
 * sprite as-is — e.g. during preview/debug).
 *
 * Caller is responsible for any rotation / transform on `ctx` before
 * the call; this helper just draws at the given rect.
 */
export function drawTintedSprite(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  tierColour: string | null,
): void {
  ctx.drawImage(image, x, y, w, h);
  if (tierColour === null) return;
  const prev = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = tierColour;
  ctx.fillRect(x, y, w, h);
  ctx.globalCompositeOperation = prev;
}

/**
 * Pick the active frame for a multi-frame sprite based on
 * elapsed-ticks-since-trigger. Static sprites (frameCount = 1) always
 * return frames[0].
 *
 * `'one-shot'` clamps at the last frame after the animation completes
 * (used for impact bursts, shockwaves, muzzle flashes). `'loop'`
 * modulo-wraps the index (used for the tileable Beam core / halo /
 * particle-trail, where the "animation" is a perpetual scrolling
 * texture).
 *
 * Returns the HTMLImageElement directly, or null if `frames` is empty
 * (defensive — shouldn't happen since the loader populates frameCount
 * entries).
 */
export function pickAnimationFrame(
  frames: HTMLImageElement[],
  elapsedTicks: number,
  ticksPerFrame: number,
  mode: AnimationMode,
): HTMLImageElement | null {
  if (frames.length === 0) return null;
  if (frames.length === 1) return frames[0]!;
  const rawIndex = Math.floor(elapsedTicks / Math.max(1, ticksPerFrame));
  const lastIndex = frames.length - 1;
  const index =
    mode === 'loop'
      ? ((rawIndex % frames.length) + frames.length) % frames.length
      : Math.min(rawIndex, lastIndex);
  return frames[index]!;
}
